import type {
  PronunciationDictionary,
  PronunciationDictionaryEntry,
  PronunciationDictionaryListPaginatedResponse,
  PronunciationDictionaryPaginatedMeta,
} from './voiceAgentTypes';

function normalizeDictionaryEntry(raw: unknown): PronunciationDictionaryEntry | null {
  if (!raw || typeof raw !== 'object') return null;
  const entry = raw as Record<string, unknown>;
  if (typeof entry.text !== 'string') return null;
  return {
    text: entry.text,
    pronunciation: typeof entry.pronunciation === 'string' ? entry.pronunciation : '',
  };
}

/** Normalize a single dictionary object from the API. */
export function normalizePronunciationDictionary(response: unknown): PronunciationDictionary {
  if (!response || typeof response !== 'object') {
    return { items: [] };
  }

  const root = response as Record<string, unknown>;
  const items = Array.isArray(root.items)
    ? root.items
        .map(normalizeDictionaryEntry)
        .filter((item): item is PronunciationDictionaryEntry => item != null)
    : [];

  const itemsCount =
    typeof root.items_count === 'number'
      ? root.items_count
      : typeof root.total_count === 'number' && (root.name || root.id)
        ? root.total_count
        : items.length;

  return {
    name: typeof root.name === 'string' ? root.name : root.name == null ? null : String(root.name),
    items,
    items_count: itemsCount,
    id: typeof root.id === 'string' ? root.id : undefined,
    provider_refs:
      typeof root.provider_refs === 'string' || root.provider_refs == null
        ? (root.provider_refs as string | null | undefined)
        : undefined,
    created_at: typeof root.created_at === 'string' ? root.created_at : undefined,
    updated_at:
      typeof root.updated_at === 'string' || root.updated_at == null
        ? (root.updated_at as string | null | undefined)
        : undefined,
  };
}

/** Normalize GET /pronunciation-dictionaries list response. */
export function normalizePronunciationDictionaryListResponse(
  response: unknown
): PronunciationDictionary[] {
  if (!response) return [];

  if (Array.isArray(response)) {
    return response.map(normalizePronunciationDictionary);
  }

  if (typeof response === 'object') {
    const root = response as Record<string, unknown>;
    if (root.data !== undefined) {
      return normalizePronunciationDictionaryListResponse(root.data);
    }
    const single = normalizePronunciationDictionary(response);
    if (single.name || single.items.length > 0 || single.id) {
      return [single];
    }
  }

  return [];
}

function extractPaginationMeta(
  root: Record<string, unknown>,
  fallbackCount: number,
  fallbackPerPage: number
): PronunciationDictionaryPaginatedMeta {
  return {
    total_count:
      typeof root.total_count === 'number'
        ? root.total_count
        : typeof root.total_items === 'number'
          ? root.total_items
          : fallbackCount,
    page: typeof root.page === 'number' ? root.page : 1,
    per_page: typeof root.per_page === 'number' ? root.per_page : fallbackPerPage,
  };
}

/** Normalize paginated GET /pronunciation-dictionaries list response. */
export function normalizePronunciationDictionaryListPaginatedResponse(
  response: unknown,
  fallbackPerPage = 10
): PronunciationDictionaryListPaginatedResponse {
  if (Array.isArray(response)) {
    const data = response.map(normalizePronunciationDictionary);
    return {
      data,
      total_count: data.length,
      page: 1,
      per_page: fallbackPerPage,
    };
  }

  if (response && typeof response === 'object') {
    const root = response as Record<string, unknown>;
    if (Array.isArray(root.data)) {
      const data = root.data.map(normalizePronunciationDictionary);
      return {
        data,
        ...extractPaginationMeta(root, data.length, fallbackPerPage),
      };
    }

    const nested = normalizePronunciationDictionaryListResponse(response);
    if (nested.length > 0) {
      return {
        data: nested,
        ...extractPaginationMeta(root, nested.length, fallbackPerPage),
      };
    }
  }

  return {
    data: [],
    total_count: 0,
    page: 1,
    per_page: fallbackPerPage,
  };
}

